import { useEffect, useRef, useState, useCallback } from 'react';

interface InactivityOptions {
  timeoutMs?: number; // Total timeout (default: 30 minutes)
  warningMs?: number; // Warning before timeout (default: 5 minutes before)
  onTimeout: () => void; // Called when timeout expires
  enabled?: boolean; // Enable/disable (default: true)
}

const CHANNEL_NAME = 'pecktree-inactivity';

/**
 * #1662 — activiteit die niet van muis of toetsenbord komt. Een GEDCOM-import
 * met plaatsverificatie duurt bij een grote stamboom een half uur; wie intussen
 * niet aan de computer zit, werd uitgelogd en verloor de import.
 */
const ACTIVITY_EVENT = 'pecktree:activity';
const KEEP_ACTIVE_INTERVAL_MS = 60 * 1000;

/** Houdt de sessie actief zolang `active` waar is (bv. een lopende import). */
export function useKeepActiveWhile(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const signal = () => window.dispatchEvent(new Event(ACTIVITY_EVENT));
    signal();
    const id = setInterval(signal, KEEP_ACTIVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active]);
}

export function useInactivityTimeout({
  timeoutMs = 30 * 60 * 1000,
  warningMs = 5 * 60 * 1000,
  onTimeout,
  enabled = true,
}: InactivityOptions) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef(Date.now());
  const channelRef = useRef<BroadcastChannel | null>(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timeoutRef.current = null;
    warningRef.current = null;
    countdownRef.current = null;
  }, []);

  const resetTimers = useCallback(
    (broadcast = true) => {
      if (!enabled) return;
      clearTimers();
      setShowWarning(false);
      lastActivityRef.current = Date.now();

      // Broadcast activity to other tabs
      if (broadcast) {
        try {
          channelRef.current?.postMessage({ type: 'activity' });
        } catch {
          /* ignored — channel may be closed */
        }
      }

      // Set warning timer
      warningRef.current = setTimeout(() => {
        setShowWarning(true);
        setRemainingSeconds(Math.ceil(warningMs / 1000));

        // Start countdown
        countdownRef.current = setInterval(() => {
          setRemainingSeconds((prev) => {
            if (prev <= 1) {
              clearTimers();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, timeoutMs - warningMs);

      // Set logout timer
      timeoutRef.current = setTimeout(() => {
        clearTimers();
        setShowWarning(false);
        onTimeout();
      }, timeoutMs);
    },
    [enabled, timeoutMs, warningMs, onTimeout, clearTimers]
  );

  const dismissWarning = useCallback(() => {
    resetTimers(true);
  }, [resetTimers]);

  // Set up BroadcastChannel for cross-tab sync
  useEffect(() => {
    if (!enabled) return;

    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;

      channel.onmessage = (event) => {
        if (event.data?.type === 'activity') {
          // Another tab had activity — reset our timers without re-broadcasting
          resetTimers(false);
        } else if (event.data?.type === 'logout') {
          // #466 multi-tab: andere tab heeft ApiService.logout() aangeroepen,
          // die al broadcast. Wij doen ALLEEN lokale cleanup + reload — GEEN
          // ApiService.logout() (dat zou opnieuw broadcast → recursie). De
          // server-sessie is al weg; App.init() op deze tab leest de
          // intent-flag en toont login.
          clearTimers();
          setShowWarning(false);
          try {
            localStorage.setItem('af_intent_logged_out', '1');
            localStorage.removeItem('af_user');
            localStorage.removeItem('af_current_project');
          } catch {
            /* ignore */
          }
          window.location.href = '/';
        }
      };

      return () => {
        channel.close();
        channelRef.current = null;
      };
    } catch {
      // BroadcastChannel not supported (older browsers) — graceful degradation
    }
  }, [enabled, resetTimers, clearTimers, onTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', ACTIVITY_EVENT];
    const handleActivity = () => {
      // Throttle: only reset if >10s since last activity
      if (Date.now() - lastActivityRef.current > 10000) {
        resetTimers(true);
      }
    };

    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    resetTimers(false); // Initial timer, don't broadcast

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      clearTimers();
    };
  }, [enabled, resetTimers, clearTimers]);

  // Expose broadcast logout for use by the timeout handler
  const broadcastLogout = useCallback(() => {
    try {
      channelRef.current?.postMessage({ type: 'logout' });
    } catch {
      /* ignored — channel may be closed */
    }
  }, []);

  return { showWarning, remainingSeconds, dismissWarning, broadcastLogout };
}
